import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FInter, FlexBox, GridBox, useBreakpoint, useToasts } from '@brg/ui';
import { API, Competition, Endpoints, LangObject, Tag } from '@brg/api';
import { Input } from '@brg/input';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LayoutConst, ModalControllerContainer } from 'core';
import { AppTester } from 'supporting';
import { 
    SendButton, 
    Wrapper , 
    ContentContainer,
    MainTitle, 
    LegalsLabel,
    FormInfoTitle,
    FormInfoDescription,
    FileInputWrapper,
    FileInput,
    EduSecetWrapper,
    ComeAgreeInfo
} from '../styles';
import { UniversitySearchBar } from 'shared/components/DropdownSearchBar/UniversitySearchBar';
import { AgreeBlock } from './components/AgreeBlock';
import { DropdownBlock } from './components/DropdownBlock';
import { DamuseumDropdown } from 'shared/components/DropdownSearchBar/DamuseumDropdown';
import { InfoIcon } from 'shared/components/InfoIcon/InfoIcon';
import { CustomForm } from './components/CustomForm';
import { CgRaveForm } from './components/CgRaveForm';
import { createDefaultLangObject } from 'shared/components/__ADDONS__/__SAFEAPI__/Defaults';

export enum DOCUMENT_VARIANT {
    none = 'none',
    short = 'short',
    full = 'full'
}

interface ProjectProps {
    projectName?: LangObject[];
    projectYear?: string;
    timing?: string;
    createCountry?: string;
    about?: LangObject[];
    hasSubtitles?: string;
    videoLink?: string;
    cloudLink?: string;
    consentToUse?: string;
    consentToDemonstrate?: string;
}

export const getDocumentVariant = (c: Competition) => 
    !!c?.nominations?.[0]?.needToFillForm && !!c?.nominations?.[0]?.documentsIds?.length  ?
    DOCUMENT_VARIANT.full : 
    !!c?.nominations?.[0]?.needToFillForm && c?.nominations?.[0]?.documentsIds?.length === 0 ? 
    DOCUMENT_VARIANT.short : 
    DOCUMENT_VARIANT.none;
/**
 * @param Модалка появляется , если в конкурсе требуется подписание документов и пользователь ранее их не подписывал @Artem
 */

interface ILegalsProps extends ProjectProps {
    fullName: string;
    email: string;
    phone: string;
    birthDate: Date | any;
    education: string;
    selecteducation: string;
    city: string;
    country: string;
    nominationInfo?: string;
    nominationsList?: any;
    projectName?: LangObject[];
    categoryInfo?: string;
    videoLink?: string;
    comeAgree?: string;
    portfolioLink?: string;
    faculty?: string;
    endYear?: string;
    occupation?: string;
    telegram?: string;
}

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/gif', 'image/png', 'application/pdf'];

const DateInputWrapper = styled.div`
    .date-input input[value=""]::-webkit-datetime-edit {
        visibility: visible;
        color: inherit;
    }
`

const HolderContent: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
    const theme = useTheme();
    return (
      <>
        <svg style={{ cursor: "pointer" }} width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="5" width="30" height="22" rx="2" stroke={theme.projectEdit.svgImgFill} strokeWidth="2"/>
          <path d="M1 26L10.3638 14.9451C10.7756 14.4589 11.531 14.4767 11.9195 14.9818L16.7735 21.2923C17.108 21.7272 17.7305 21.8113 18.1685 21.4808L21.103 19.2664C21.4632 18.9945 21.9609 18.9977 22.3177 19.2741L31 26" stroke={theme.projectEdit.svgImgFill} strokeWidth="2"/>
          <path d="M27 12C27 13.6569 25.6569 15 24 15C22.3431 15 21 13.6569 21 12C21 10.3431 22.3431 9 24 9C25.6569 9 27 10.3431 27 12Z" stroke={theme.projectEdit.svgImgFill} strokeWidth="2"/>
        </svg>
        <FlexBox
          offsetTop={6}
          justify={"center"}
          style={{ textAlign: "center" }}
        >
          {children}
        </FlexBox>
      </>
    );
  };

export const CompetitionLegalsModal: FC<{
    projects: string[],
    nominationIds: string[],
    competitionId?: string, 
    callback: () => void,
    hasDocuments?: boolean,

    }> = 
    ({projects, nominationIds, hasDocuments, competitionId, callback }) => {
    const { t, i18n: { language } } = useTranslation();
    const { error } = useToasts();
    const theme = useTheme();
    const form = useRef(null);
    const isDamuseum = AppTester.isDaMuseum();
    const isCGRave = AppTester.isCGRAVE();
    const isMobile = useBreakpoint(LayoutConst.screen.s -1 );
    const [fileUpload, setFileUpload] = useState<boolean>(false);
    const [agreeSign, setAgreeSign] = useState(false);
    const [agreeData, setAgreeData] = useState(null);

    const isCoverCompetition = useMemo(
        () => competitionId ? (competitionId === 'b20fce3f2826462c932db5e68865af09') : undefined , [competitionId])

    const isVideoCompetition = useMemo(
        () => competitionId ? (competitionId === '0439ccbea0944f25a6ea380c0c465bd6') : undefined , [competitionId])

    const isFlowerCompetition = useMemo(
        () => competitionId ? (competitionId === '715e8cb236894348abc4d87d40069242') : undefined , [competitionId])

    const isBlueRibbonCompetition = useMemo(
        () => competitionId ? (competitionId === '93da3243d9354e62aaf404f097d955f3') : undefined , [competitionId])

    const isSmallShortCompetitions = useMemo(
        () => competitionId ? [
            '1d3562abcb02489bb7b18379aeaf40b9', 
            'e25c7245287548188be20de82b93a365', 
            '111a74097f0948dfb50b5258676fb5d3', 
            '5e0f05e63e8b4122931caf06bf824beb']
            ?.includes(competitionId) 
        : undefined 
    , [competitionId])

    const isCgRaveCompetitions = useMemo(
        () => {
            if (isCGRave && competitionId) {
                return  [
                    '88ee8a75a6834c1bae76a1c59b48a1f6',
                    '140f49e2fdb84d16a5c357f062301301',
                    '504c441499254f15bf92fce90966ae21',
                    'd045fe37c89441bda082708eb21cd473',
                    '3bdef2729f0f499faa69f983c92d8bd5',
                    '53657ff8da3147b887e4949d1e3ab153',
                    '0a375c53f2294df39b911b687d48d550',
                    'e31b1534709945cda40ac4d42dd1790c',
    
                ]?.includes(competitionId)
            }
            return false;
        }, [competitionId, isCGRave])

    const isCgRaveStudentsCompetitions = useMemo(
        () => {
            if (isCGRave && competitionId) {
                return  [
                    '3646e2b8abb34f318e7bc220a23e19c1'
    
                ]?.includes(competitionId)
            }
            return false;
        }, [competitionId, isCGRave])

    const [data, setData] = useState<ILegalsProps>({
        fullName: '',
        email: '',
        phone: '',
        birthDate: '',
        education: '',
        selecteducation: '',
        city: '',
        country: '',
        nominationInfo: '',
        categoryInfo: '',
        ...(isVideoCompetition ? {
            videoLink: '',
            comeAgree: 'false',
        } : {}),
        ...(isFlowerCompetition ? {
            portfolioLink: '',
            faculty: '',
            endYear: ''
        } : {}),
        ...(isCgRaveCompetitions ? {
            occupation: ''
        } : {}),
        ...(isBlueRibbonCompetition ? {
            telegram: ''
        } : {}),
        ...(isSmallShortCompetitions ? {
            nominationsList: [
                {
                    text:  'Screen I. Игровое и документальное кино',
                    value: 'undefined'
                },
                {
                    text:  'Screen II. Экспериментальное видео и видеоарт',
                    value: 'undefined'
                },
                {
                    text:  'Screen III. Анимация и генеративная графика',
                    value: 'undefined'
                },
            ],
            projectName: createDefaultLangObject(''),
            projectYear: '',
            timing: '',
            createCountry: '',
            about: createDefaultLangObject(''),
            hasSubtitles: 'false',
            videoLink: '',
            cloudLink: '',
            consentToUse: 'false',
            consentToDemonstrate: 'false',
        } : {})
    });

    const [projectsId, setProjectId] = useState<string[]>([]);

    const isGeo = AppTester.isGeograffee();

    const someNomination = useMemo(() => (nominationIds?.length > 1), [nominationIds])

    const currentDate = useMemo(() => {
        const date = new Date().toLocaleDateString();
        return date.split('.').reverse().join().replaceAll(',','-' )
    } , [])

    const comeAgreeTranslate = useMemo(
        () => (data.comeAgree === 'false' ? (language.toLowerCase() ==='ru' ? 'Нет' : 'No') : (language.toLowerCase() === 'rn' ? 'Yes' : 'Да')) , [language, data.comeAgree])


    useEffect(() => {
        if (projects) {
            setProjectId(projects)
        }
    },[projects])

    const checkDate = (s: string) => {
        const reg = new RegExp('^[0-9]{4}[.|-][0-9]{2}[.|-][0-9]{2}$');
        return reg.test(s);
    }

    const checkEmail = (s: string) => {
        return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(s);
    }

    const checkVideoLinks = (s:string) => {
        const youbeReg = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const vimeoReg = /^(http|https)?:\/\/?(vimeo.com)/;
        const vkReg = /^(https:\/\/)?(vk.com)/;
        const vkVideoReg = /^(https:\/\/)?(vkvideo.ru)/;  

        return youbeReg.test(s) || vimeoReg.test(s) || vkReg.test(s) || vkVideoReg.test(s);
    }

    const handleUpdateFullnamelChange = (e: any) =>setData({...data, fullName: e.target.value});

    const handleUpdateOccupationChange = (e: any) => setData({...data, occupation: e.target.value});

    const handleUpdateEmailChange = (e: any) => setData({...data, email: e.target.value});

    const handleUpdateCityChange = (e: any) => setData({...data, city: e.target.value});

    const handleUpdateCountryChange = (e: any) => setData({...data, country: e.target.value});

    const handleUpdateVideoLinkChange = (e: any) => setData({...data, videoLink: e.target.value});

    const handleUpdateFacultyChange = (e: any) => setData({...data, faculty: e.target.value});

    const handleUpdateEndYearChange = (e: any) => setData({...data, endYear: e.target.value});

    const handleUpdatePortfolioLinkChange = (e: any) => setData({...data, portfolioLink: e.target.value});

    const handleUpdatePhoneChange = (e: any) => setData({...data, phone: e.target.value});
    
    const handleUpdateDateChange = (e: any) => setData({...data, birthDate: e.target.value});
    
    const handleUpdateEducationChange = (val: string) => setData({...data, education: val, selecteducation: '' });

    const handleUpdateAdditionalInfo = (value: string, key: string) => setData({...data, [key]: value });

    const handleUpdateData = (ev: React.BaseSyntheticEvent, key: string, lang?: string, activeIdx?: number) => {

        if (activeIdx !== undefined) {
            //@ts-ignore
            const nData = data?.[key]?.map((i, idx) => {
                if (idx === activeIdx) {
                    return { ...i, value: 'true'}
                }
                    
                else {
                    return {...i, value: undefined}
                }
            });

            setData({...data, [key]: nData });
            return;
        }
        if (lang) {
            //@ts-ignore
            const cIdx = data?.[key]?.findIndex((i) => i?.lang === lang);

             //@ts-ignore
            const nData = data?.[key]?.map((i, idx) => {
                if (idx === cIdx) {
                    return { lang, name: ev.target.value}
                }
                    
                else {
                    return i
                }
            });

            setData({...data, [key]: nData });
            return
        }

        setData({...data, [key]: ev.target.value });
    }

    const handleUpdateSelectEducationChange = ({ tagId, name }: Tag) => { 
        setData({...data, selecteducation: tagId, education: name});
    };

    const handleUpdateFilesChange = (e: any) => e.target.files.length > 0 && setFileUpload(true);

    const handleUpdateComeAgreeChange = (value: string) => { 
        setData({...data, comeAgree: value === 'Yes' || value === 'Да' ? String(true) : String(false)});
    };

    const handleAgreeDataSign = (flag: boolean, data: any ) => {
        setAgreeSign(flag)
        setAgreeData(data)
    }

    const onDrop = (e: React.DragEvent) => {
        const files = Array.from(e.dataTransfer.files);
        const uploadIsAllowed = files.every(({type}) => ALLOWED_FILE_TYPES.includes(type));
        if (!uploadIsAllowed) {
          e.preventDefault();
          e.stopPropagation();
          error(t('allowed_file_types'));
        }
    }

    const customFormProps = useMemo(() => {
        if (isBlueRibbonCompetition) {
            return {
                fullName: data.fullName,
                email: data.email,
                phone: data.phone,
                telegram: data.telegram,
                competitionId: competitionId ?? '',
                onAgreeSign: (flag: boolean, data: any) => handleAgreeDataSign(flag, data),
                onDataChange: handleUpdateData,
                onEducationChange: handleUpdateEducationChange,
                onSelectedEduChange: handleUpdateSelectEducationChange,
                checkboxes: [
                    { 
                        text: (language === 'ru' ? (
                            <span>Я соглашаюсь с <a href={'https://drive.google.com/file/d/1HFzNVhsT-pqU50E77ISBtzwlcJjqI1ga/view'} target={'_BLANK'}>положением о конкурсе</a></span>
                        ) : (
                            <span>I agree to <a href={'https://drive.google.com/file/d/1HFzNVhsT-pqU50E77ISBtzwlcJjqI1ga/view'} target={'_BLANK'}>the competition rules</a></span>
                        ))
                    },
                    { 
                        text: (language === 'ru' ? (
                            <span>Я соглашаюсь с <a href={'https://drive.google.com/file/d/1Ys3fCmMV4aFndqhG66tKRQt4WlqChmnq/view?usp=sharing'} target={'_BLANK'}>политикой информационной безопасности ООО "ЭМГ"</a></span>
                        ) : (
                            <span>I agree to <a href={'https://drive.google.com/file/d/1Ys3fCmMV4aFndqhG66tKRQt4WlqChmnq/view?usp=sharing'} target={'_BLANK'}>the information security policy of LLC 'EMG'</a></span>
                        ))
                    }
                ]}
            } else if (isSmallShortCompetitions) {
                return {
                    onAgreeSign: (flag: boolean, data: any) => handleAgreeDataSign(flag, data),
                    onEducationChange: handleUpdateEducationChange,
                    onSelectedEduChange: handleUpdateSelectEducationChange,
                    onDataChange: handleUpdateData,
                    fullName: data.fullName,
                    email: data.email,
                    nominationsList: data?.nominationsList,
                    projectName: data?.projectName,
                    projectYear: data?.projectYear,
                    timing: data?.timing,
                    createCountry: data?.createCountry,
                    about: data?.about,
                    hasSubtitles: data?.hasSubtitles,
                    videoLink: data?.videoLink,
                    cloudLink: data?.cloudLink,
                    consentToUse: data?.consentToUse,
                    consentToDemonstrate: data?.consentToDemonstrate,
                    checkboxes: [
                        { 
                            text: <span>{t('designer_edit.fields.consentToDemonstrate')}</span>
                        },
                    ]
                }
            } else if (isCoverCompetition) {
                return {
                    fullName: data.fullName,
                    email: data.email,
                    phone: data.phone,
                    birthDate: data.birthDate,
                    country: data.country,
                    city: data.city,
                    competitionId: competitionId ?? '',
                    onAgreeSign: (flag: boolean, data: any) => handleAgreeDataSign(flag, data),
                    onSelectedEduChange: handleUpdateSelectEducationChange,
                    onEducationChange: handleUpdateEducationChange,
                    onDataChange: handleUpdateData,
                    onDateChange: handleUpdateDateChange,
                    checkboxes: [
                        {
                            text: language === 'ru' ? (
                            <span>Я соглашаюсь с <a href={process.env.REACT_APP_URL_HSEDESIGN_RU  + '/legals'} target={'_BLANK'}>Пользовательским соглашением</a> ресурсов hsedesign.com и hsedesign.ru</span>
                            ) : (
                                <span>I agree to the <a href={process.env.REACT_APP_URL_HSEDESIGN_RU  + '/legals'} target={'_BLANK'}>User Agreement</a> of the resources hsedesign.com and hsedesign.ru</span>
                            )
                        },
                        {
                            text: language === 'ru' ? (
                            <span>Я соглашаюсь с <a href={'https://www.hse.ru/data_protection_regulation'} target={'_BLANK'}>Политикой конфиденциальности и обработки персональных данных</a></span>
                            ) : (
                                <span>I agree to <a href={'https://www.hse.ru/data_protection_regulation'} target={'_BLANK'}>the Privacy and Personal Data Processing Policy</a></span>
                            )},
                        {
                            text: language === 'ru' ? (
                                <span>Я принимаю условия <a href={'https://message2man.com/privacy-policy/'} target={'_BLANK'}>Политики конфиденциальности</a></span>
                            ) : (
                                <span>I accept the terms of<a href={'https://message2man.com/privacy-policy/'} target={'_BLANK'}>the Privacy Policy</a></span>
                            )
                        }
                    ]

                }
            }

            else {
                return {}
            }
        
    } , [data])

    const onSaveClick = useCallback((e: React.SyntheticEvent) => {
        e.preventDefault();
        
        if (!checkDate(data.birthDate) && !isCgRaveCompetitions && !isCgRaveStudentsCompetitions && !isBlueRibbonCompetition && !isSmallShortCompetitions) {
            error(t('reports.date_format'));
            return null;
        }

        if (!checkEmail(data?.email)) {
            error(t('reports.email_format'));
            return null;
        }

        if (isSmallShortCompetitions  && !checkVideoLinks(data?.videoLink ?? '')) {
            error(t('reports.wrong_video_link'));
            return null;
        }

        if (data && form && form.current) {
            const formData = new FormData(form.current);
            const formatted = 
                Object.entries(data).map(([key,val]) => ({
                    Title: key,
                    Value: val
                }))?.filter((item) => {
                    if (isCgRaveCompetitions || isCgRaveStudentsCompetitions || isBlueRibbonCompetition) {
                        return [
                            'fullName', 
                            'email', 
                            'occupation', 
                            'country',
                            'education',
                            'selectededucation',
                            'telegram',
                            'phone'
                        ]?.includes(item?.Title)
                    } 

                    return item;
                })?.map((item) => {
                    if (['about', 'projectName', 'nominationsList']?.includes(item?.Title)) {
                        return {...item, Value: JSON.stringify(item?.Value)}
                    }
                    return item
                })
            const documentsFormat = agreeData ? Object.entries(agreeData)?.map(([k,v]) => {
                return {
                    'Name': k,
                    'IsSigned': v
                }
            }) : null

            formData.append('Fields', JSON.stringify(formatted));

            formData.set('NominationId', JSON.stringify(nominationIds));

            formData.set('CompetitionId', competitionId ?? '');

            if (documentsFormat) {
                formData.append('Documents',  JSON.stringify(documentsFormat))
            }

            const deletedFields = ['videoLink', 'portfolioLink', 'endYear', 'faculty', 'date', 'city', 'country']

            deletedFields?.forEach((field) => formData.delete(field))

            if (agreeSign) {
                API.fetch(
                    Endpoints.competitions + '/api/Competition/ApplyNomination', {
                        method: "POST",
                        body: formData 
                })
                .then((r) => {
                    if (r.status === 200) {
                        API.fetch(
                            Endpoints.competitions + '/api/Competition/AddCompetitorFormAnswer', {
                                method: "POST",
                                body: formData 
                        })
                        callback();
                    }
                })
                .catch((e) => error(String(e)))
            } else {
                error(t('reports.agree_sign_error'));
            }
        }
    }, [
        data,
        agreeData,
        isCgRaveCompetitions,
        isCgRaveStudentsCompetitions,
        isSmallShortCompetitions,
        isBlueRibbonCompetition,
        someNomination, 
        nominationIds, 
        competitionId,
        projects,
        t, 
        callback, 
        error
    ]);

    const defaultNominationId = useMemo(() => nominationIds?.[0] , [nominationIds])

    const importantCustomBG = useMemo(() => (isCGRave ? { importantCustomBG: 'rgb(0,0,0)'} : {}) , [isCGRave])

    const disableSubmit = useMemo(
        () => {
            if (isSmallShortCompetitions) {
                return !agreeSign || !data.cloudLink?.length || !data?.videoLink?.length || !data?.nominationsList?.some((i: any) => i?.value === 'true') ||
                !data?.fullName?.length || !data?.timing?.length || !data?.email?.length || data?.projectName?.some((i) => i?.name === '') || !data?.projectYear?.length ||
                !data?.createCountry?.length || data?.about?.some((i) => i?.name === '')
            }

            return (isDamuseum && (!agreeSign || (!data?.nominationInfo?.length || !data?.categoryInfo?.length))) || ((isCgRaveCompetitions || isCgRaveStudentsCompetitions || isBlueRibbonCompetition) && !agreeSign) 
        } , [agreeSign, isBlueRibbonCompetition, isCgRaveStudentsCompetitions, isCgRaveCompetitions, data, isDamuseum])

    return <ModalControllerContainer {...importantCustomBG}>
        <Wrapper direction='column' style={{ margin: isMobile ? '12px auto' : '88px auto', overflowY: "auto", overflowX: 'hidden' }} isGeo={isGeo} >
            <ContentContainer>
            <MainTitle>{t('competition.application_form')}</MainTitle>
            <form ref={form} onSubmit={(e) => onSaveClick(e)} encType="multipart/form-data" id="submit-form">
                <input type='text' name='ProjectId' value={projectsId[0]} hidden />
                <input type='text' name='NominationId' value={defaultNominationId} hidden />
                <FlexBox justify={'center'} direction='column' directionMobile='column' offsetRight={20} offsetRightMobile={1}>
                        {(isCgRaveCompetitions || isCgRaveStudentsCompetitions) ? (
                            <CgRaveForm 
                                onFullNameChange={handleUpdateFullnamelChange}
                                onCountryChange={handleUpdateCountryChange}
                                onEmailChange={handleUpdateEmailChange}
                                onAgreeSign={(flag: boolean, arr: any) => handleAgreeDataSign(flag, arr)}
                                fullName={data.fullName}
                                email={data.email}
                                country={data.country}
                                occupation={data.occupation}
                                competitionId={competitionId ?? ''}
                                {...(isCgRaveStudentsCompetitions ? { 
                                    onEducationChange: handleUpdateEducationChange,
                                    onSelectedEduChange: handleUpdateSelectEducationChange
                                } : {
                                    onUserOccupatioChange: handleUpdateOccupationChange
                                })}
                            />
                        ) : (isBlueRibbonCompetition || isSmallShortCompetitions || isCoverCompetition) ? (
                            /**Для новых анкет использовать этот компонент */
                            <CustomForm 
                                {...customFormProps}
                            />
                        ) : (
                            <>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.fullname')}</LegalsLabel>
                                    <Input.Text
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                                        onChange={(e) => handleUpdateFullnamelChange(e)}
                                        value={data.fullName}
                                        title={`${t('reports.only_word')}`}
                                        data-type="fullName"
                                        maxLength={100}
                                        required
                                    />
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.email')}</LegalsLabel>
                                    <Input.Text
                                        type='email'
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdateEmailChange(e)}
                                        value={data.email}
                                        data-type="email"
                                        required
                                    />
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.phone')}</LegalsLabel>
                                    <Input.Text
                                        type="tel"
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdatePhoneChange(e)}
                                        value={data.phone}
                                        data-type="phone"
                                        title='89008007060'
                                        pattern="[0-9]{1}[0-9]{3}[0-9]{3}[0-9]{4}"
                                        required
                                    />
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.birth_date')}</LegalsLabel>
                                    <DateInputWrapper>
                                    <Input.Text
                                        type="date"
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdateDateChange(e)}
                                        value={data.birthDate}
                                        pattern='^[0-9]{2}[.|-][0-9]{2}[.|-][0-9]{4}$'
                                        name="date"
                                        className={'date-input'}
                                        title="Формат даты: 00.00.0000"
                                        data-type="date"
                                        max={currentDate}
                                        required
                                    /></DateInputWrapper>
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                                    <LegalsLabel>{t('designer_edit.fields.education_center')}</LegalsLabel>

                                    <EduSecetWrapper>
                                        <UniversitySearchBar
                                            onAddClick={handleUpdateEducationChange}
                                            onChooseClick={handleUpdateSelectEducationChange}
                                            editing
                                            placeholder=''
                                        />
                                    </EduSecetWrapper>
                                </GridBox>
                                {isFlowerCompetition && (
                                    <>
                                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                            <LegalsLabel>{t('designer_edit.fields.faculty')}</LegalsLabel>
                                            <Input.Text
                                                type="text"
                                                style={{
                                                    backgroundColor: theme.competition.input.bg,
                                                    color: theme.competition.input.mainText,
                                                }}
                                                onChange={(e) => handleUpdateFacultyChange(e)}
                                                value={data.faculty}
                                                pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                                                name="faculty"
                                                className={'date-input'}
                                                title={`${t('reports.only_word')}`}
                                                data-type="faculty"
                                                required
                                            />
                                        </GridBox>
                                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                            <LegalsLabel>{t('designer_edit.fields.endYear')}</LegalsLabel>
                                            <Input.Text
                                                type="text"
                                                style={{
                                                    backgroundColor: theme.competition.input.bg,
                                                    color: theme.competition.input.mainText,
                                                }}
                                                onChange={(e) => handleUpdateEndYearChange(e)}
                                                value={data.endYear}
                                                pattern='^[0-9]+$'
                                                name="endYear"
                                                className={'date-input'}
                                                title={`${t('reports.only_number')}`}
                                                data-type="endYear"
                                                required
                                            />
                                        </GridBox>
                                    </>
                                )}
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.city')}</LegalsLabel>
                                    <Input.Text
                                        type="text"
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdateCityChange(e)}
                                        value={data.city}
                                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                                        name="city"
                                        className={'date-input'}
                                        title={`${t('reports.only_word')}`}
                                        data-type="city"
                                        max={currentDate}
                                        required
                                    />
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.country')}</LegalsLabel>
                                    <Input.Text
                                        type="text"
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdateCountryChange(e)}
                                        value={data.country}
                                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                                        name="country"
                                        className={'date-input'}
                                        title={`${t('reports.only_word')}`}
                                        data-type="country"
                                        max={currentDate}
                                        required
                                    />
                                </GridBox>
                                {isFlowerCompetition && (
                                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                        <LegalsLabel>{t('designer_edit.fields.portfolioLink')}</LegalsLabel>
                                        <Input.Text
                                            type="text"
                                            style={{
                                                backgroundColor: theme.competition.input.bg,
                                                color: theme.competition.input.mainText,
                                            }}
                                            onChange={(e) => handleUpdatePortfolioLinkChange(e)}
                                            value={data.portfolioLink}
                                            pattern='^(ftp|http|https):\/\/[^ "]+$'
                                            name="portfolioLink"
                                            className={'date-input'}
                                            title={`${t('reports.link')}`}
                                            data-type="portfolioLink"
                                            required
                                        />
                                    </GridBox>
                                )}
                    
                    {isVideoCompetition && (
                        <>
                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                            <LegalsLabel>
                                {t('designer_edit.fields.video_link')}
                                <InfoIcon msg={t('designer_edit.fields.video_link_info')}/>
                                </LegalsLabel>
                            <Input.Text
                                type="text"
                                style={{
                                    backgroundColor: theme.competition.input.bg,
                                    color: theme.competition.input.mainText,
                                }}
                                onChange={(e) => handleUpdateVideoLinkChange(e)}
                                value={data.videoLink}
                                name="videoLink"
                                className={'video-link-input'}
                                data-type="video-link"
                                required
                            />
                        </GridBox>
                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                            <LegalsLabel>{t('designer_edit.fields.come_agree')}</LegalsLabel>
                            <FlexBox direction='column' directionMobile='column'>
                                <ComeAgreeInfo className='come-agree-info'>{t('designer_edit.fields.come_agree_info')}</ComeAgreeInfo>
                                <EduSecetWrapper>
                                    <DamuseumDropdown 
                                        editing={true}
                                        onAddClick={(ev:any) => console.log('add', ev)}
                                        onChooseClick={(ev: any) => console.log('choose',ev)}
                                        currentValue={comeAgreeTranslate}
                                        valueList={language === 'ru' ? ['Да', 'Нет'] : ['Yes', 'No']}
                                        onValueChange={handleUpdateComeAgreeChange}
                                    />
                                </EduSecetWrapper> 
                            </FlexBox>
                        </GridBox>                       
                        </>
                    )}

                    <DropdownBlock onSetAdditionalInfo={handleUpdateAdditionalInfo} />
                    {hasDocuments && (
                        <GridBox style={{ justifyContent: "space-between", alignItems: 'center' }} cols={'1fr'} gap={10}>
                            <FlexBox direction='column' directionMobile='column' offsetTop={16} /*offsetLeftMobile={16} offsetRightMobile={16}*/>
                                <FormInfoTitle color={theme.sendProjectsModal.title}>
                                    Форма согласия на обработку персональных данных <br /> участника творческого конкурса
                                </FormInfoTitle>
                                <FormInfoDescription style={{ padding: "32px 0 16px 0"}}>
                                    Если Вам есть 18 лет, <a href="https://drive.google.com/file/d/1x9PBMss7pnj1dwsjGPTMRxjyNRfOABtX/view" target="_blank">скачайте документ</a> и заполните его. Отсканируйте и загрузите заполненный документ.
                                </FormInfoDescription>
                                <FormInfoDescription>
                                    Если Вам нет 18 лет, <a href="https://drive.google.com/file/d/1IiTAkxmZagPU11Yw1i6aNAzcLgAjqk76/view" target="_blank">скачайте документ</a>, Ваш законный представитель должен заполнить его. Отсканируйте и загрузите заполненный документ.
                                </FormInfoDescription>
                                <FileInputWrapper 
                                    style={{ marginTop: "20px" }}
                                    customBackgroundColor={theme.projectEdit.imgContainerBg}>
                                    <FileInput
                                        type="file"
                                        onChange={(e) => handleUpdateFilesChange(e)}
                                        onDrop={onDrop}
                                        style={{ cursor: "pointer" }}
                                        accept="image/jpeg, image/gif, image/png, application/pdf"
                                        name='files'
                                        required
                                    />
                                    <HolderContent>
                                        <FInter.SmallText color={theme.sendProjectsModal.title}>{fileUpload ? 'Файл загружен' : 'Загрузить файл'}</FInter.SmallText>
                                    </HolderContent>
                                </FileInputWrapper>
                            </FlexBox>
                        </GridBox>
                    )}
                    <AgreeBlock onCheck={(flag: boolean, data: any) => handleAgreeDataSign(flag, data)} competitionId={competitionId} />
                </>
                )}
 
                </FlexBox>
                <FlexBox justify={'flex-end'} direction='row' directionMobile={isMobile ? 'column' : 'row'} offsetTop={40} offsetRight={20} offsetRightMobile={1}>
                    <SendButton isGreen={isGeo} type='submit' mr={"10"} disabled={disableSubmit}>
                        {t('competition.submit_form')}
                    </SendButton>
                </FlexBox>
            </form>
            </ContentContainer>
        </Wrapper>
    </ModalControllerContainer>

};
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FInter, FlexBox, GridBox, useBreakpoint, useToasts } from '@brg/ui';
import { API, Competition, Endpoints, LangObject, Tag } from '@brg/api';
import { Input } from '@brg/input';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LayoutConst, ModalControllerContainer } from 'core';
import { AppTester } from 'supporting';
import { 
    SendButton, 
    Wrapper , 
    ContentContainer,
    MainTitle, 
    LegalsLabel,
    FormInfoTitle,
    FormInfoDescription,
    FileInputWrapper,
    FileInput,
    EduSecetWrapper,
    ComeAgreeInfo
} from '../styles';
import { UniversitySearchBar } from 'shared/components/DropdownSearchBar/UniversitySearchBar';
import { AgreeBlock } from './components/AgreeBlock';
import { DropdownBlock } from './components/DropdownBlock';
import { DamuseumDropdown } from 'shared/components/DropdownSearchBar/DamuseumDropdown';
import { InfoIcon } from 'shared/components/InfoIcon/InfoIcon';
import { CustomForm } from './components/CustomForm';
import { CgRaveForm } from './components/CgRaveForm';
import { createDefaultLangObject } from 'shared/components/__ADDONS__/__SAFEAPI__/Defaults';

export enum DOCUMENT_VARIANT {
    none = 'none',
    short = 'short',
    full = 'full'
}

interface ProjectProps {
    projectName?: LangObject[];
    projectYear?: string;
    timing?: string;
    createCountry?: string;
    about?: LangObject[];
    hasSubtitles?: string;
    videoLink?: string;
    cloudLink?: string;
    consentToUse?: string;
    consentToDemonstrate?: string;
}

export const getDocumentVariant = (c: Competition) => 
    !!c?.nominations?.[0]?.needToFillForm && !!c?.nominations?.[0]?.documentsIds?.length  ?
    DOCUMENT_VARIANT.full : 
    !!c?.nominations?.[0]?.needToFillForm && c?.nominations?.[0]?.documentsIds?.length === 0 ? 
    DOCUMENT_VARIANT.short : 
    DOCUMENT_VARIANT.none;
/**
 * @param Модалка появляется , если в конкурсе требуется подписание документов и пользователь ранее их не подписывал @Artem
 */

interface ILegalsProps extends ProjectProps {
    fullName: string;
    email: string;
    phone: string;
    birthDate: Date | any;
    education: string;
    selecteducation: string;
    city: string;
    country: string;
    nominationInfo?: string;
    nominationsList?: any;
    projectName?: LangObject[];
    categoryInfo?: string;
    videoLink?: string;
    comeAgree?: string;
    portfolioLink?: string;
    faculty?: string;
    endYear?: string;
    occupation?: string;
    telegram?: string;
}

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/gif', 'image/png', 'application/pdf'];

const DateInputWrapper = styled.div`
    .date-input input[value=""]::-webkit-datetime-edit {
        visibility: visible;
        color: inherit;
    }
`

const HolderContent: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
    const theme = useTheme();
    return (
      <>
        <svg style={{ cursor: "pointer" }} width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="5" width="30" height="22" rx="2" stroke={theme.projectEdit.svgImgFill} strokeWidth="2"/>
          <path d="M1 26L10.3638 14.9451C10.7756 14.4589 11.531 14.4767 11.9195 14.9818L16.7735 21.2923C17.108 21.7272 17.7305 21.8113 18.1685 21.4808L21.103 19.2664C21.4632 18.9945 21.9609 18.9977 22.3177 19.2741L31 26" stroke={theme.projectEdit.svgImgFill} strokeWidth="2"/>
          <path d="M27 12C27 13.6569 25.6569 15 24 15C22.3431 15 21 13.6569 21 12C21 10.3431 22.3431 9 24 9C25.6569 9 27 10.3431 27 12Z" stroke={theme.projectEdit.svgImgFill} strokeWidth="2"/>
        </svg>
        <FlexBox
          offsetTop={6}
          justify={"center"}
          style={{ textAlign: "center" }}
        >
          {children}
        </FlexBox>
      </>
    );
  };

export const CompetitionLegalsModal: FC<{
    projects: string[],
    nominationIds: string[],
    competitionId?: string, 
    callback: () => void,
    hasDocuments?: boolean,

    }> = 
    ({projects, nominationIds, hasDocuments, competitionId, callback }) => {
    const { t, i18n: { language } } = useTranslation();
    const { error } = useToasts();
    const theme = useTheme();
    const form = useRef(null);
    const isDamuseum = AppTester.isDaMuseum();
    const isCGRave = AppTester.isCGRAVE();
    const isMobile = useBreakpoint(LayoutConst.screen.s -1 );
    const [fileUpload, setFileUpload] = useState<boolean>(false);
    const [agreeSign, setAgreeSign] = useState(false);
    const [agreeData, setAgreeData] = useState(null);

    const isCoverCompetition = useMemo(
        () => competitionId ? (competitionId === 'b20fce3f2826462c932db5e68865af09') : undefined , [competitionId])

    const isVideoCompetition = useMemo(
        () => competitionId ? (competitionId === '0439ccbea0944f25a6ea380c0c465bd6') : undefined , [competitionId])

    const isFlowerCompetition = useMemo(
        () => competitionId ? (competitionId === '715e8cb236894348abc4d87d40069242') : undefined , [competitionId])

    const isBlueRibbonCompetition = useMemo(
        () => competitionId ? (competitionId === '93da3243d9354e62aaf404f097d955f3') : undefined , [competitionId])

    const isSmallShortCompetitions = useMemo(
        () => competitionId ? [
            '1d3562abcb02489bb7b18379aeaf40b9', 
            'e25c7245287548188be20de82b93a365', 
            '111a74097f0948dfb50b5258676fb5d3', 
            '5e0f05e63e8b4122931caf06bf824beb']
            ?.includes(competitionId) 
        : undefined 
    , [competitionId])

    const isCgRaveCompetitions = useMemo(
        () => {
            if (isCGRave && competitionId) {
                return  [
                    '88ee8a75a6834c1bae76a1c59b48a1f6',
                    '140f49e2fdb84d16a5c357f062301301',
                    '504c441499254f15bf92fce90966ae21',
                    'd045fe37c89441bda082708eb21cd473',
                    '3bdef2729f0f499faa69f983c92d8bd5',
                    '53657ff8da3147b887e4949d1e3ab153',
                    '0a375c53f2294df39b911b687d48d550',
                    'e31b1534709945cda40ac4d42dd1790c',
    
                ]?.includes(competitionId)
            }
            return false;
        }, [competitionId, isCGRave])

    const isCgRaveStudentsCompetitions = useMemo(
        () => {
            if (isCGRave && competitionId) {
                return  [
                    '3646e2b8abb34f318e7bc220a23e19c1'
    
                ]?.includes(competitionId)
            }
            return false;
        }, [competitionId, isCGRave])

    const [data, setData] = useState<ILegalsProps>({
        fullName: '',
        email: '',
        phone: '',
        birthDate: '',
        education: '',
        selecteducation: '',
        city: '',
        country: '',
        nominationInfo: '',
        categoryInfo: '',
        ...(isVideoCompetition ? {
            videoLink: '',
            comeAgree: 'false',
        } : {}),
        ...(isFlowerCompetition ? {
            portfolioLink: '',
            faculty: '',
            endYear: ''
        } : {}),
        ...(isCgRaveCompetitions ? {
            occupation: ''
        } : {}),
        ...(isBlueRibbonCompetition ? {
            telegram: ''
        } : {}),
        ...(isSmallShortCompetitions ? {
            nominationsList: [
                {
                    text:  'Screen I. Игровое и документальное кино',
                    value: 'undefined'
                },
                {
                    text:  'Screen II. Экспериментальное видео и видеоарт',
                    value: 'undefined'
                },
                {
                    text:  'Screen III. Анимация и генеративная графика',
                    value: 'undefined'
                },
            ],
            projectName: createDefaultLangObject(''),
            projectYear: '',
            timing: '',
            createCountry: '',
            about: createDefaultLangObject(''),
            hasSubtitles: 'false',
            videoLink: '',
            cloudLink: '',
            consentToUse: 'false',
            consentToDemonstrate: 'false',
        } : {})
    });

    const [projectsId, setProjectId] = useState<string[]>([]);

    const isGeo = AppTester.isGeograffee();

    const someNomination = useMemo(() => (nominationIds?.length > 1), [nominationIds])

    const currentDate = useMemo(() => {
        const date = new Date().toLocaleDateString();
        return date.split('.').reverse().join().replaceAll(',','-' )
    } , [])

    const comeAgreeTranslate = useMemo(
        () => (data.comeAgree === 'false' ? (language.toLowerCase() ==='ru' ? 'Нет' : 'No') : (language.toLowerCase() === 'rn' ? 'Yes' : 'Да')) , [language, data.comeAgree])


    useEffect(() => {
        if (projects) {
            setProjectId(projects)
        }
    },[projects])

    const checkDate = (s: string) => {
        const reg = new RegExp('^[0-9]{4}[.|-][0-9]{2}[.|-][0-9]{2}$');
        return reg.test(s);
    }

    const checkEmail = (s: string) => {
        return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(s);
    }

    const checkVideoLinks = (s:string) => {
        const youbeReg = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const vimeoReg = /^(http|https)?:\/\/?(vimeo.com)/;
        const vkReg = /^(https:\/\/)?(vk.com)/;
        const vkVideoReg = /^(https:\/\/)?(vkvideo.ru)/;  

        return youbeReg.test(s) || vimeoReg.test(s) || vkReg.test(s) || vkVideoReg.test(s);
    }

    const handleUpdateFullnamelChange = (e: any) =>setData({...data, fullName: e.target.value});

    const handleUpdateOccupationChange = (e: any) => setData({...data, occupation: e.target.value});

    const handleUpdateEmailChange = (e: any) => setData({...data, email: e.target.value});

    const handleUpdateCityChange = (e: any) => setData({...data, city: e.target.value});

    const handleUpdateCountryChange = (e: any) => setData({...data, country: e.target.value});

    const handleUpdateVideoLinkChange = (e: any) => setData({...data, videoLink: e.target.value});

    const handleUpdateFacultyChange = (e: any) => setData({...data, faculty: e.target.value});

    const handleUpdateEndYearChange = (e: any) => setData({...data, endYear: e.target.value});

    const handleUpdatePortfolioLinkChange = (e: any) => setData({...data, portfolioLink: e.target.value});

    const handleUpdatePhoneChange = (e: any) => setData({...data, phone: e.target.value});
    
    const handleUpdateDateChange = (e: any) => setData({...data, birthDate: e.target.value});
    
    const handleUpdateEducationChange = (val: string) => setData({...data, education: val, selecteducation: '' });

    const handleUpdateAdditionalInfo = (value: string, key: string) => setData({...data, [key]: value });

    const handleUpdateData = (ev: React.BaseSyntheticEvent, key: string, lang?: string, activeIdx?: number) => {

        if (activeIdx !== undefined) {
            //@ts-ignore
            const nData = data?.[key]?.map((i, idx) => {
                if (idx === activeIdx) {
                    return { ...i, value: 'true'}
                }
                    
                else {
                    return {...i, value: undefined}
                }
            });

            setData({...data, [key]: nData });
            return;
        }
        if (lang) {
            //@ts-ignore
            const cIdx = data?.[key]?.findIndex((i) => i?.lang === lang);

             //@ts-ignore
            const nData = data?.[key]?.map((i, idx) => {
                if (idx === cIdx) {
                    return { lang, name: ev.target.value}
                }
                    
                else {
                    return i
                }
            });

            setData({...data, [key]: nData });
            return
        }

        setData({...data, [key]: ev.target.value });
    }

    const handleUpdateSelectEducationChange = ({ tagId, name }: Tag) => { 
        setData({...data, selecteducation: tagId, education: name});
    };

    const handleUpdateFilesChange = (e: any) => e.target.files.length > 0 && setFileUpload(true);

    const handleUpdateComeAgreeChange = (value: string) => { 
        setData({...data, comeAgree: value === 'Yes' || value === 'Да' ? String(true) : String(false)});
    };

    const handleAgreeDataSign = (flag: boolean, data: any ) => {
        setAgreeSign(flag)
        setAgreeData(data)
    }

    const onDrop = (e: React.DragEvent) => {
        const files = Array.from(e.dataTransfer.files);
        const uploadIsAllowed = files.every(({type}) => ALLOWED_FILE_TYPES.includes(type));
        if (!uploadIsAllowed) {
          e.preventDefault();
          e.stopPropagation();
          error(t('allowed_file_types'));
        }
    }

    const customFormProps = useMemo(() => {
        if (isBlueRibbonCompetition) {
            return {
                fullName: data.fullName,
                email: data.email,
                phone: data.phone,
                telegram: data.telegram,
                competitionId: competitionId ?? '',
                onAgreeSign: (flag: boolean, data: any) => handleAgreeDataSign(flag, data),
                onDataChange: handleUpdateData,
                onEducationChange: handleUpdateEducationChange,
                onSelectedEduChange: handleUpdateSelectEducationChange,
                checkboxes: [
                    { 
                        text: (language === 'ru' ? (
                            <span>Я соглашаюсь с <a href={'https://drive.google.com/file/d/1HFzNVhsT-pqU50E77ISBtzwlcJjqI1ga/view'} target={'_BLANK'}>положением о конкурсе</a></span>
                        ) : (
                            <span>I agree to <a href={'https://drive.google.com/file/d/1HFzNVhsT-pqU50E77ISBtzwlcJjqI1ga/view'} target={'_BLANK'}>the competition rules</a></span>
                        ))
                    },
                    { 
                        text: (language === 'ru' ? (
                            <span>Я соглашаюсь с <a href={'https://drive.google.com/file/d/1Ys3fCmMV4aFndqhG66tKRQt4WlqChmnq/view?usp=sharing'} target={'_BLANK'}>политикой информационной безопасности ООО "ЭМГ"</a></span>
                        ) : (
                            <span>I agree to <a href={'https://drive.google.com/file/d/1Ys3fCmMV4aFndqhG66tKRQt4WlqChmnq/view?usp=sharing'} target={'_BLANK'}>the information security policy of LLC 'EMG'</a></span>
                        ))
                    }
                ]}
            } else if (isSmallShortCompetitions) {
                return {
                    onAgreeSign: (flag: boolean, data: any) => handleAgreeDataSign(flag, data),
                    onEducationChange: handleUpdateEducationChange,
                    onSelectedEduChange: handleUpdateSelectEducationChange,
                    onDataChange: handleUpdateData,
                    fullName: data.fullName,
                    email: data.email,
                    nominationsList: data?.nominationsList,
                    projectName: data?.projectName,
                    projectYear: data?.projectYear,
                    timing: data?.timing,
                    createCountry: data?.createCountry,
                    about: data?.about,
                    hasSubtitles: data?.hasSubtitles,
                    videoLink: data?.videoLink,
                    cloudLink: data?.cloudLink,
                    consentToUse: data?.consentToUse,
                    consentToDemonstrate: data?.consentToDemonstrate,
                    checkboxes: [
                        { 
                            text: <span>{t('designer_edit.fields.consentToDemonstrate')}</span>
                        },
                    ]
                }
            } else if (isCoverCompetition) {
                return {
                    fullName: data.fullName,
                    email: data.email,
                    phone: data.phone,
                    birthDate: data.birthDate,
                    country: data.country,
                    city: data.city,
                    competitionId: competitionId ?? '',
                    onAgreeSign: (flag: boolean, data: any) => handleAgreeDataSign(flag, data),
                    onSelectedEduChange: handleUpdateSelectEducationChange,
                    onEducationChange: handleUpdateEducationChange,
                    onDataChange: handleUpdateData,
                    onDateChange: handleUpdateDateChange,
                    checkboxes: [
                        {
                            text: language === 'ru' ? (
                            <span>Я соглашаюсь с <a href={process.env.REACT_APP_URL_HSEDESIGN_RU  + '/legals'} target={'_BLANK'}>Пользовательским соглашением</a> ресурсов hsedesign.com и hsedesign.ru</span>
                            ) : (
                                <span>I agree to the <a href={process.env.REACT_APP_URL_HSEDESIGN_RU  + '/legals'} target={'_BLANK'}>User Agreement</a> of the resources hsedesign.com and hsedesign.ru</span>
                            )
                        },
                        {
                            text: language === 'ru' ? (
                            <span>Я соглашаюсь с <a href={'https://www.hse.ru/data_protection_regulation'} target={'_BLANK'}>Политикой конфиденциальности и обработки персональных данных</a></span>
                            ) : (
                                <span>I agree to <a href={'https://www.hse.ru/data_protection_regulation'} target={'_BLANK'}>the Privacy and Personal Data Processing Policy</a></span>
                            )},
                        {
                            text: language === 'ru' ? (
                                <span>Я принимаю условия <a href={'https://message2man.com/privacy-policy/'} target={'_BLANK'}>Политики конфиденциальности</a></span>
                            ) : (
                                <span>I accept the terms of<a href={'https://message2man.com/privacy-policy/'} target={'_BLANK'}>the Privacy Policy</a></span>
                            )
                        }
                    ]

                }
            }

            else {
                return {}
            }
        
    } , [data])

    const onSaveClick = useCallback((e: React.SyntheticEvent) => {
        e.preventDefault();
        
        if (!checkDate(data.birthDate) && !isCgRaveCompetitions && !isCgRaveStudentsCompetitions && !isBlueRibbonCompetition && !isSmallShortCompetitions) {
            error(t('reports.date_format'));
            return null;
        }

        if (!checkEmail(data?.email)) {
            error(t('reports.email_format'));
            return null;
        }

        if (isSmallShortCompetitions  && !checkVideoLinks(data?.videoLink ?? '')) {
            error(t('reports.wrong_video_link'));
            return null;
        }

        if (data && form && form.current) {
            const formData = new FormData(form.current);
            const formatted = 
                Object.entries(data).map(([key,val]) => ({
                    Title: key,
                    Value: val
                }))?.filter((item) => {
                    if (isCgRaveCompetitions || isCgRaveStudentsCompetitions || isBlueRibbonCompetition) {
                        return [
                            'fullName', 
                            'email', 
                            'occupation', 
                            'country',
                            'education',
                            'selectededucation',
                            'telegram',
                            'phone'
                        ]?.includes(item?.Title)
                    } 

                    return item;
                })?.map((item) => {
                    if (['about', 'projectName', 'nominationsList']?.includes(item?.Title)) {
                        return {...item, Value: JSON.stringify(item?.Value)}
                    }
                    return item
                })
            const documentsFormat = agreeData ? Object.entries(agreeData)?.map(([k,v]) => {
                return {
                    'Name': k,
                    'IsSigned': v
                }
            }) : null

            formData.append('Fields', JSON.stringify(formatted));

            formData.set('NominationId', JSON.stringify(nominationIds));

            formData.set('CompetitionId', competitionId ?? '');

            if (documentsFormat) {
                formData.append('Documents',  JSON.stringify(documentsFormat))
            }

            const deletedFields = ['videoLink', 'portfolioLink', 'endYear', 'faculty', 'date', 'city', 'country']

            deletedFields?.forEach((field) => formData.delete(field))

            if (agreeSign) {
                API.fetch(
                    Endpoints.competitions + '/api/Competition/ApplyNomination', {
                        method: "POST",
                        body: formData 
                })
                .then((r) => {
                    if (r.status === 200) {
                        API.fetch(
                            Endpoints.competitions + '/api/Competition/AddCompetitorFormAnswer', {
                                method: "POST",
                                body: formData 
                        })
                        callback();
                    }
                })
                .catch((e) => error(String(e)))
            } else {
                error(t('reports.agree_sign_error'));
            }
        }
    }, [
        data,
        agreeData,
        isCgRaveCompetitions,
        isCgRaveStudentsCompetitions,
        isSmallShortCompetitions,
        isBlueRibbonCompetition,
        someNomination, 
        nominationIds, 
        competitionId,
        projects,
        t, 
        callback, 
        error
    ]);

    const defaultNominationId = useMemo(() => nominationIds?.[0] , [nominationIds])

    const importantCustomBG = useMemo(() => (isCGRave ? { importantCustomBG: 'rgb(0,0,0)'} : {}) , [isCGRave])

    const disableSubmit = useMemo(
        () => {
            if (isSmallShortCompetitions) {
                return !agreeSign || !data.cloudLink?.length || !data?.videoLink?.length || !data?.nominationsList?.some((i: any) => i?.value === 'true') ||
                !data?.fullName?.length || !data?.timing?.length || !data?.email?.length || data?.projectName?.some((i) => i?.name === '') || !data?.projectYear?.length ||
                !data?.createCountry?.length || data?.about?.some((i) => i?.name === '')
            }

            return (isDamuseum && (!agreeSign || (!data?.nominationInfo?.length || !data?.categoryInfo?.length))) || ((isCgRaveCompetitions || isCgRaveStudentsCompetitions || isBlueRibbonCompetition) && !agreeSign) 
        } , [agreeSign, isBlueRibbonCompetition, isCgRaveStudentsCompetitions, isCgRaveCompetitions, data, isDamuseum])

    return <ModalControllerContainer {...importantCustomBG}>
        <Wrapper direction='column' style={{ margin: isMobile ? '12px auto' : '88px auto', overflowY: "auto", overflowX: 'hidden' }} isGeo={isGeo} >
            <ContentContainer>
            <MainTitle>{t('competition.application_form')}</MainTitle>
            <form ref={form} onSubmit={(e) => onSaveClick(e)} encType="multipart/form-data" id="submit-form">
                <input type='text' name='ProjectId' value={projectsId[0]} hidden />
                <input type='text' name='NominationId' value={defaultNominationId} hidden />
                <FlexBox justify={'center'} direction='column' directionMobile='column' offsetRight={20} offsetRightMobile={1}>
                        {(isCgRaveCompetitions || isCgRaveStudentsCompetitions) ? (
                            <CgRaveForm 
                                onFullNameChange={handleUpdateFullnamelChange}
                                onCountryChange={handleUpdateCountryChange}
                                onEmailChange={handleUpdateEmailChange}
                                onAgreeSign={(flag: boolean, arr: any) => handleAgreeDataSign(flag, arr)}
                                fullName={data.fullName}
                                email={data.email}
                                country={data.country}
                                occupation={data.occupation}
                                competitionId={competitionId ?? ''}
                                {...(isCgRaveStudentsCompetitions ? { 
                                    onEducationChange: handleUpdateEducationChange,
                                    onSelectedEduChange: handleUpdateSelectEducationChange
                                } : {
                                    onUserOccupatioChange: handleUpdateOccupationChange
                                })}
                            />
                        ) : (isBlueRibbonCompetition || isSmallShortCompetitions || isCoverCompetition) ? (
                            /**Для новых анкет использовать этот компонент */
                            <CustomForm 
                                {...customFormProps}
                            />
                        ) : (
                            <>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.fullname')}</LegalsLabel>
                                    <Input.Text
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                                        onChange={(e) => handleUpdateFullnamelChange(e)}
                                        value={data.fullName}
                                        title={`${t('reports.only_word')}`}
                                        data-type="fullName"
                                        maxLength={100}
                                        required
                                    />
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.email')}</LegalsLabel>
                                    <Input.Text
                                        type='email'
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdateEmailChange(e)}
                                        value={data.email}
                                        data-type="email"
                                        required
                                    />
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.phone')}</LegalsLabel>
                                    <Input.Text
                                        type="tel"
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdatePhoneChange(e)}
                                        value={data.phone}
                                        data-type="phone"
                                        title='89008007060'
                                        pattern="[0-9]{1}[0-9]{3}[0-9]{3}[0-9]{4}"
                                        required
                                    />
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.birth_date')}</LegalsLabel>
                                    <DateInputWrapper>
                                    <Input.Text
                                        type="date"
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdateDateChange(e)}
                                        value={data.birthDate}
                                        pattern='^[0-9]{2}[.|-][0-9]{2}[.|-][0-9]{4}$'
                                        name="date"
                                        className={'date-input'}
                                        title="Формат даты: 00.00.0000"
                                        data-type="date"
                                        max={currentDate}
                                        required
                                    /></DateInputWrapper>
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                                    <LegalsLabel>{t('designer_edit.fields.education_center')}</LegalsLabel>

                                    <EduSecetWrapper>
                                        <UniversitySearchBar
                                            onAddClick={handleUpdateEducationChange}
                                            onChooseClick={handleUpdateSelectEducationChange}
                                            editing
                                            placeholder=''
                                        />
                                    </EduSecetWrapper>
                                </GridBox>
                                {isFlowerCompetition && (
                                    <>
                                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                            <LegalsLabel>{t('designer_edit.fields.faculty')}</LegalsLabel>
                                            <Input.Text
                                                type="text"
                                                style={{
                                                    backgroundColor: theme.competition.input.bg,
                                                    color: theme.competition.input.mainText,
                                                }}
                                                onChange={(e) => handleUpdateFacultyChange(e)}
                                                value={data.faculty}
                                                pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                                                name="faculty"
                                                className={'date-input'}
                                                title={`${t('reports.only_word')}`}
                                                data-type="faculty"
                                                required
                                            />
                                        </GridBox>
                                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                            <LegalsLabel>{t('designer_edit.fields.endYear')}</LegalsLabel>
                                            <Input.Text
                                                type="text"
                                                style={{
                                                    backgroundColor: theme.competition.input.bg,
                                                    color: theme.competition.input.mainText,
                                                }}
                                                onChange={(e) => handleUpdateEndYearChange(e)}
                                                value={data.endYear}
                                                pattern='^[0-9]+$'
                                                name="endYear"
                                                className={'date-input'}
                                                title={`${t('reports.only_number')}`}
                                                data-type="endYear"
                                                required
                                            />
                                        </GridBox>
                                    </>
                                )}
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.city')}</LegalsLabel>
                                    <Input.Text
                                        type="text"
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdateCityChange(e)}
                                        value={data.city}
                                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                                        name="city"
                                        className={'date-input'}
                                        title={`${t('reports.only_word')}`}
                                        data-type="city"
                                        max={currentDate}
                                        required
                                    />
                                </GridBox>
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                    <LegalsLabel>{t('designer_edit.fields.country')}</LegalsLabel>
                                    <Input.Text
                                        type="text"
                                        style={{
                                            backgroundColor: theme.competition.input.bg,
                                            color: theme.competition.input.mainText,
                                        }}
                                        onChange={(e) => handleUpdateCountryChange(e)}
                                        value={data.country}
                                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                                        name="country"
                                        className={'date-input'}
                                        title={`${t('reports.only_word')}`}
                                        data-type="country"
                                        max={currentDate}
                                        required
                                    />
                                </GridBox>
                                {isFlowerCompetition && (
                                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                                        <LegalsLabel>{t('designer_edit.fields.portfolioLink')}</LegalsLabel>
                                        <Input.Text
                                            type="text"
                                            style={{
                                                backgroundColor: theme.competition.input.bg,
                                                color: theme.competition.input.mainText,
                                            }}
                                            onChange={(e) => handleUpdatePortfolioLinkChange(e)}
                                            value={data.portfolioLink}
                                            pattern='^(ftp|http|https):\/\/[^ "]+$'
                                            name="portfolioLink"
                                            className={'date-input'}
                                            title={`${t('reports.link')}`}
                                            data-type="portfolioLink"
                                            required
                                        />
                                    </GridBox>
                                )}
                    
                    {isVideoCompetition && (
                        <>
                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                            <LegalsLabel>
                                {t('designer_edit.fields.video_link')}
                                <InfoIcon msg={t('designer_edit.fields.video_link_info')}/>
                                </LegalsLabel>
                            <Input.Text
                                type="text"
                                style={{
                                    backgroundColor: theme.competition.input.bg,
                                    color: theme.competition.input.mainText,
                                }}
                                onChange={(e) => handleUpdateVideoLinkChange(e)}
                                value={data.videoLink}
                                name="videoLink"
                                className={'video-link-input'}
                                data-type="video-link"
                                required
                            />
                        </GridBox>
                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                            <LegalsLabel>{t('designer_edit.fields.come_agree')}</LegalsLabel>
                            <FlexBox direction='column' directionMobile='column'>
                                <ComeAgreeInfo className='come-agree-info'>{t('designer_edit.fields.come_agree_info')}</ComeAgreeInfo>
                                <EduSecetWrapper>
                                    <DamuseumDropdown 
                                        editing={true}
                                        onAddClick={(ev:any) => console.log('add', ev)}
                                        onChooseClick={(ev: any) => console.log('choose',ev)}
                                        currentValue={comeAgreeTranslate}
                                        valueList={language === 'ru' ? ['Да', 'Нет'] : ['Yes', 'No']}
                                        onValueChange={handleUpdateComeAgreeChange}
                                    />
                                </EduSecetWrapper> 
                            </FlexBox>
                        </GridBox>                       
                        </>
                    )}

                    <DropdownBlock onSetAdditionalInfo={handleUpdateAdditionalInfo} />
                    {hasDocuments && (
                        <GridBox style={{ justifyContent: "space-between", alignItems: 'center' }} cols={'1fr'} gap={10}>
                            <FlexBox direction='column' directionMobile='column' offsetTop={16} /*offsetLeftMobile={16} offsetRightMobile={16}*/>
                                <FormInfoTitle color={theme.sendProjectsModal.title}>
                                    Форма согласия на обработку персональных данных <br /> участника творческого конкурса
                                </FormInfoTitle>
                                <FormInfoDescription style={{ padding: "32px 0 16px 0"}}>
                                    Если Вам есть 18 лет, <a href="https://drive.google.com/file/d/1x9PBMss7pnj1dwsjGPTMRxjyNRfOABtX/view" target="_blank">скачайте документ</a> и заполните его. Отсканируйте и загрузите заполненный документ.
                                </FormInfoDescription>
                                <FormInfoDescription>
                                    Если Вам нет 18 лет, <a href="https://drive.google.com/file/d/1IiTAkxmZagPU11Yw1i6aNAzcLgAjqk76/view" target="_blank">скачайте документ</a>, Ваш законный представитель должен заполнить его. Отсканируйте и загрузите заполненный документ.
                                </FormInfoDescription>
                                <FileInputWrapper 
                                    style={{ marginTop: "20px" }}
                                    customBackgroundColor={theme.projectEdit.imgContainerBg}>
                                    <FileInput
                                        type="file"
                                        onChange={(e) => handleUpdateFilesChange(e)}
                                        onDrop={onDrop}
                                        style={{ cursor: "pointer" }}
                                        accept="image/jpeg, image/gif, image/png, application/pdf"
                                        name='files'
                                        required
                                    />
                                    <HolderContent>
                                        <FInter.SmallText color={theme.sendProjectsModal.title}>{fileUpload ? 'Файл загружен' : 'Загрузить файл'}</FInter.SmallText>
                                    </HolderContent>
                                </FileInputWrapper>
                            </FlexBox>
                        </GridBox>
                    )}
                    <AgreeBlock onCheck={(flag: boolean, data: any) => handleAgreeDataSign(flag, data)} competitionId={competitionId} />
                </>
                )}
 
                </FlexBox>
                <FlexBox justify={'flex-end'} direction='row' directionMobile={isMobile ? 'column' : 'row'} offsetTop={40} offsetRight={20} offsetRightMobile={1}>
                    <SendButton isGreen={isGeo} type='submit' mr={"10"} disabled={disableSubmit}>
                        {t('competition.submit_form')}
                    </SendButton>
                </FlexBox>
            </form>
            </ContentContainer>
        </Wrapper>
    </ModalControllerContainer>

};
